// RH-PAIE-1 — Centre de qualité des dossiers salariaux.
// Vue RH paginée (jamais les 142 employés chargés d'un coup) pour identifier et corriger,
// un par un, les dossiers salariaux incomplets ou en attente de validation.
import { useState, useEffect, useCallback } from 'react';
import { libelleSite } from '../utils/organisation';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { humaniserFamilleProfessionnelle } from '../constants/libellesHumains';

const API = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';
const VERT = '#1B5E20';
const ROUGE = '#C62828';
const ORANGE = '#E65100';
const GRIS_BORD = '#E0E0E0';
const GRIS_TEXTE = '#666';
// RH-PAIE-2F §8 — la catégorie seule est ambiguë (même code possible dans plusieurs familles).
const fmtCategorie = (e) => {
  if (!e?.categorie) return '–';
  return `${e.famille_professionnelle ? humaniserFamilleProfessionnelle(e.famille_professionnelle) : 'Famille professionnelle : à classer'} — Catégorie ${e.categorie}`;
};

const STATUTS = [
  { v: '', l: 'Tous les statuts' },
  { v: 'COMPLET', l: 'Complet' },
  { v: 'INCOMPLET', l: 'Incomplet' },
  { v: 'A_VALIDER', l: 'À valider' },
  { v: 'PROGRAMME', l: 'Programmé' },
  { v: 'SANS_PROFIL', l: 'Sans profil' },
];
const COULEUR_STATUT = { COMPLET: VERT, INCOMPLET: ROUGE, A_VALIDER: ORANGE, PROGRAMME: ORANGE, SANS_PROFIL: ROUGE };

function Badge({ texte, couleur }) {
  return <span style={{ background: couleur + '1a', color: couleur, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{texte}</span>;
}

function Compteur({ label, valeur, couleur }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${GRIS_BORD}`, borderRadius: 10, padding: '14px 16px', flex: '1 1 140px', minWidth: 130 }}>
      <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: couleur || '#222' }}>{valeur ?? '–'}</div>
    </div>
  );
}

export default function DossierSalarialQualitePage() {
  const navigate = useNavigate();
  const { employe: userConnecte } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState('');
  const [page, setPage] = useState(1);
  const [recherche, setRecherche] = useState('');
  const [statutFiltre, setStatutFiltre] = useState('');
  const [siteFiltre, setSiteFiltre] = useState('');
  // Module 0 : filtre par identifiant de site (liste du référentiel), jamais par code saisi.
  const [sitesRef, setSitesRef] = useState([]);
  useEffect(() => {
    axios.get(`${API}/referentiel/sites`).then((r) => setSitesRef(r.data?.sites || [])).catch(() => setSitesRef([]));
  }, []);

  const charger = useCallback(async () => {
    setLoading(true); setErreur('');
    try {
      const params = { page, limite: 20 };
      if (recherche) params.recherche = recherche;
      if (statutFiltre) params.statut_dossier = statutFiltre;
      if (siteFiltre) params.site_id = siteFiltre;
      const { data: res } = await axios.get(`${API}/dossiers-salariaux/qualite`, { params });
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 403) {
        setErreur("Vous n'avez pas les droits nécessaires pour consulter cette page. Contactez un administrateur RH si vous pensez qu'il s'agit d'une erreur.");
      } else {
        setErreur(e.response?.data?.message || 'Impossible de charger le centre de qualité.');
      }
    } finally { setLoading(false); }
  }, [page, recherche, statutFiltre, siteFiltre]);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => { setPage(1); }, [recherche, statutFiltre, siteFiltre]);

  const ROLES_EXPORT = ['drh', 'rh_principal'];
  const peutExporter = ROLES_EXPORT.includes(userConnecte?.role) || userConnecte?.permissions_effectives?.peut_exporter_dossier_salarial === true;

  const exporter = async () => {
    try {
      const params = {};
      if (statutFiltre) params.statut_dossier = statutFiltre;
      if (siteFiltre) params.site_id = siteFiltre;
      const { data: res } = await axios.get(`${API}/dossiers-salariaux/qualite/export`, { params });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `dossiers-salariaux-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErreur(e.response?.data?.message || 'Impossible d\'exporter.');
    }
  };

  return (
    <Layout>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '20px 4px', fontFamily: 'Inter, Segoe UI, sans-serif' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ color: VERT, fontSize: 19, margin: '0 0 4px' }}>Centre de qualité des dossiers salariaux</h2>
            <p style={{ color: GRIS_TEXTE, fontSize: 13, margin: '0 0 18px' }}>
              Aucune catégorie ni aucun poste n'est jamais rempli automatiquement — cette vue aide à corriger chaque dossier, un par un, avec preuve et date d'effet.
            </p>
          </div>
          {peutExporter && (
            <button onClick={exporter} style={{ background: '#fff', border: `1px solid ${VERT}`, color: VERT, borderRadius: 8, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              Exporter
            </button>
          )}
        </div>

        {data?.compteurs && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
            <Compteur label="Total employés actifs" valeur={data.compteurs.total} />
            <Compteur label="Complets" valeur={data.compteurs.complet} couleur={VERT} />
            <Compteur label="Incomplets" valeur={data.compteurs.incomplet} couleur={ROUGE} />
            <Compteur label="À valider" valeur={data.compteurs.a_valider} couleur={ORANGE} />
            <Compteur label="Sans profil" valeur={data.compteurs.sans_profil} couleur={ROUGE} />
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <input placeholder="Rechercher (nom, prénom, matricule)" value={recherche} onChange={(e) => setRecherche(e.target.value)}
            style={{ flex: '1 1 220px', padding: '8px 12px', border: `1px solid ${GRIS_BORD}`, borderRadius: 8, fontSize: 13 }} />
          <select value={statutFiltre} onChange={(e) => setStatutFiltre(e.target.value)}
            style={{ padding: '8px 12px', border: `1px solid ${GRIS_BORD}`, borderRadius: 8, fontSize: 13, background: '#fff' }}>
            {STATUTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
          <select value={siteFiltre} onChange={(e) => setSiteFiltre(e.target.value)}
            style={{ padding: '8px 12px', border: `1px solid ${GRIS_BORD}`, borderRadius: 8, fontSize: 13, background: '#fff' }}>
            <option value="">Tous les sites</option>
            {sitesRef.map((s) => <option key={s.id} value={s.id}>{libelleSite(s)}</option>)}
          </select>
        </div>

        {erreur && <div style={{ color: ROUGE, fontSize: 13, marginBottom: 12 }}>{erreur}</div>}
        {loading ? (
          <div style={{ color: GRIS_TEXTE, fontSize: 13 }}>Chargement…</div>
        ) : (
          <>
            <style>{`
              .dsq-wrap { overflow-x: auto; width: 100%; max-width: 100%; box-sizing: border-box; }
              .dsq-table { width: 100%; border-collapse: collapse; font-size: 12.5px; background: #fff; border: 1px solid ${GRIS_BORD}; border-radius: 10px; overflow: hidden; }
              .dsq-table th { text-align: left; padding: 10px 12px; color: #888; font-size: 10.5px; text-transform: uppercase; letter-spacing: .04em; border-bottom: 2px solid ${GRIS_BORD}; }
              .dsq-table td { padding: 10px 12px; border-bottom: 1px solid #f0f0f0; }
              .dsq-cards { display: none; }
              @media (max-width: 767px) {
                .dsq-wrap { overflow-x: visible; }
                .dsq-table { display: none; }
                .dsq-cards { display: block; }
              }
            `}</style>
            <div className="dsq-wrap">
              <table className="dsq-table">
                <thead>
                  <tr><th>Employé</th><th>Site</th><th>Catégorie</th><th>Date d'effet</th><th>Statut</th><th></th></tr>
                </thead>
                <tbody>
                  {(data?.items || []).map((e) => (
                    <tr key={e.id}>
                      <td>{e.prenom} {e.nom} <span style={{ color: '#999' }}>· {e.matricule}</span></td>
                      <td>{e.site_code || '–'}</td>
                      <td>{fmtCategorie(e)}</td>
                      <td>{e.date_effet ? new Date(e.date_effet).toLocaleDateString('fr-FR') : '–'}</td>
                      <td><Badge texte={e.statut_dossier} couleur={COULEUR_STATUT[e.statut_dossier] || GRIS_TEXTE} /></td>
                      <td>
                        <button onClick={() => navigate(`/employes/${e.id}?onglet=paie`)}
                          style={{ background: 'none', border: `1px solid ${VERT}`, color: VERT, borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                          Corriger
                        </button>
                      </td>
                    </tr>
                  ))}
                  {(!data?.items || data.items.length === 0) && (
                    <tr><td colSpan={6} style={{ textAlign: 'center', color: GRIS_TEXTE, padding: 24 }}>Aucun résultat.</td></tr>
                  )}
                </tbody>
              </table>

              <div className="dsq-cards">
                {(data?.items || []).map((e) => (
                  <div key={e.id} style={{ border: `1px solid ${GRIS_BORD}`, borderRadius: 10, padding: 12, marginBottom: 10, background: '#fff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <strong style={{ fontSize: 13 }}>{e.prenom} {e.nom}</strong>
                      <Badge texte={e.statut_dossier} couleur={COULEUR_STATUT[e.statut_dossier] || GRIS_TEXTE} />
                    </div>
                    <div style={{ fontSize: 12, color: GRIS_TEXTE, marginBottom: 8 }}>{e.matricule} · {e.site_code || '–'} · {e.categorie ? fmtCategorie(e) : 'Catégorie non renseignée'}</div>
                    <button onClick={() => navigate(`/employes/${e.id}?onglet=paie`)}
                      style={{ width: '100%', minHeight: 40, background: 'none', border: `1px solid ${VERT}`, color: VERT, borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                      Corriger le dossier
                    </button>
                  </div>
                ))}
                {(!data?.items || data.items.length === 0) && (
                  <div style={{ textAlign: 'center', color: GRIS_TEXTE, padding: 24 }}>Aucun résultat.</div>
                )}
              </div>
            </div>

            {data && data.pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
                <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                  style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${GRIS_BORD}`, background: '#fff', fontSize: 12, cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1 }}>Précédent</button>
                <span style={{ fontSize: 12, color: GRIS_TEXTE, alignSelf: 'center' }}>Page {data.page} / {data.pages}</span>
                <button disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}
                  style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${GRIS_BORD}`, background: '#fff', fontSize: 12, cursor: page >= data.pages ? 'not-allowed' : 'pointer', opacity: page >= data.pages ? 0.5 : 1 }}>Suivant</button>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
